import React, { useMemo, useCallback, useState, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import { useAtelierStore, JOB_STATUSES, getCachedWorkers } from "@store/atelierStore";
import { WorkshopJob, WorkshopJobStatus, CarWindow } from "../../types/garage/atelier";
import { AvailabilityBlock } from "@store/disponibiliteStore";
import { formatTime } from "@utils/timeGrid";
import {
  GridPreview,
  JobDragPreview,
  HOURS_START,
  HOURS_END,
  SNAP_PX,
  SNAP_HOUR,
  HOUR_BLOCK_HEIGHT,
  LUNCH_HOUR,
} from "./gridTypes";
import OrdreModal from "@components/modals/OrdreModal";
import { findOrdreForPlate, buildOrdreFromAppointment, getOrdreById } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import type { Ordre } from "types/ordre";

const CLICK_THRESHOLD = 5;

function getStatusConfig(status: WorkshopJobStatus) {
  return JOB_STATUSES.find((s) => s.id === status)!;
}

/** Get day-of-week index (0=Mon … 6=Sun) from YYYY-MM-DD */
function dayOfWeekIndex(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00");
  const jsDay = d.getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** Check if a worker has a block covering a specific hour on a specific date */
function isBlockedByDispo(
  blocks: AvailabilityBlock[],
  workerId: string,
  date: string,
  hour: number,
): boolean {
  const dow = dayOfWeekIndex(date);
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const matches = b.date === date || (b.recurring && b.dayOfWeek === dow);
    if (matches) {
      const blockEnd = b.startHour + b.durationHours;
      if (hour >= b.startHour && hour < blockEnd) return true;
    }
  }
  return false;
}

export function WorkerTimeGrid({
  gridScrollRef,
  gridPreview,
  setGridPreview,
  jobDragRef,
  blocks = [],
}: {
  gridScrollRef: React.RefObject<HTMLDivElement | null>;
  gridPreview: GridPreview | null;
  setGridPreview: (p: GridPreview | null) => void;
  jobDragRef: React.MutableRefObject<{
    jobId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origWorkerId: string;
  } | null>;
  blocks?: AvailabilityBlock[];
}) {
  const selectedDate = useAtelierStore((s) => s.selectedDate);
  const getFilteredJobs = useAtelierStore((s) => s.getFilteredJobs);
  const getVisibleWorkers = useAtelierStore((s) => s.getVisibleWorkers);
  const hoveredChargeId = useAtelierStore((s) => s.hoveredChargeId);
  const charges = useAtelierStore((s) => s.charges);
  const storeCarWindows = useAtelierStore((s) => s.carWindows);

  const jobs = getFilteredJobs();
  const visibleWorkers = getVisibleWorkers();

  // Resolve carWindow: from hovered charge OR from the job being dragged
  const activeCarWindow = useMemo((): CarWindow | null => {
    // Priority 1: hovered charge in sidebar
    if (hoveredChargeId) {
      const charge = charges.find((c) => c.id === hoveredChargeId);
      return charge?.carWindow || null;
    }
    // Priority 2: job being dragged — look up carWindow from job, charges, or persistent store
    if (gridPreview?.type === "job") {
      const job = jobs.find((j) => j.id === (gridPreview as JobDragPreview).jobId) ||
        useAtelierStore.getState().jobs.find((j) => j.id === (gridPreview as JobDragPreview).jobId);
      if (job?.carWindow) return job.carWindow;
      if (job?.ordre_id) {
        const charge = charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow);
        return charge?.carWindow || storeCarWindows[job.ordre_id] || null;
      }
    }
    return null;
  }, [hoveredChargeId, charges, gridPreview, jobs, storeCarWindows]);

  // Compute car availability overlay for the day view
  const carOverlay = useMemo(() => {
    if (!activeCarWindow) return null;
    const cw = activeCarWindow;
    if (!cw.depositDate) return null;
    const restDate = cw.restitutionDate || cw.depositDate;
    if (selectedDate < cw.depositDate || selectedDate > restDate) return null;
    const startHour = selectedDate === cw.depositDate && cw.depositStartHour != null
      ? cw.depositStartHour : HOURS_START;
    const endHour = selectedDate === restDate && cw.restitutionStartHour != null
      ? cw.restitutionStartHour : HOURS_END;
    return { startHour, endHour };
  }, [activeCarWindow, selectedDate]);

  // Use persistent carWindows from store for conflict detection (survives charge deletion)
  const carWindowByOrdre = storeCarWindows;

  /** Check if a job is (partially) outside the car's presence window */
  const isJobOutsideCarWindow = useCallback((job: WorkshopJob): boolean => {
    if (!job.ordre_id) return false;
    const cw = carWindowByOrdre[job.ordre_id];
    if (!cw || !cw.depositDate) return false;
    const restDate = cw.restitutionDate || cw.depositDate;
    // Job is on a day entirely outside the car window
    if (job.date < cw.depositDate || job.date > restDate) return true;
    // Same-day checks
    const jobEnd = job.startHour + job.durationHours;
    if (job.date === cw.depositDate && cw.depositStartHour != null && job.startHour < cw.depositStartHour) return true;
    if (job.date === restDate && cw.restitutionStartHour != null && jobEnd > cw.restitutionStartHour) return true;
    return false;
  }, [carWindowByOrdre]);

  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [hoveredJobId, setHoveredJobId] = useState<string | null>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);
  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  const byWorker = useMemo(() => {
    const map: Record<string, WorkshopJob[]> = {};
    for (const w of getCachedWorkers()) map[w.id] = [];
    for (const j of jobs) {
      if (map[j.workerId]) map[j.workerId].push(j);
    }
    return map;
  }, [jobs]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, job: WorkshopJob, mode: "move" | "resize") => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      clickStartRef.current = { x: e.clientX, y: e.clientY };
      jobDragRef.current = {
        jobId: job.id,
        mode,
        startY: e.clientY,
        origStartHour: job.startHour,
        origDuration: job.durationHours,
        origWorkerId: job.workerId,
      };
      setGridPreview({
        type: "job",
        jobId: job.id,
        startHour: job.startHour,
        durationHours: job.durationHours,
        workerId: job.workerId,
      });
    },
    [setGridPreview, jobDragRef],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent, job: WorkshopJob) => {
      const start = clickStartRef.current;
      if (
        start &&
        Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
        Math.abs(e.clientY - start.y) < CLICK_THRESHOLD
      ) {
        if (job.ordre_id) {
          ordresApi.get(job.ordre_id).then((ordre) => setSelectedOrdre(ordre)).catch(() => {
            setSelectedOrdre(findOrdreForPlate(job.plate) || buildOrdreFromAppointment(job));
          });
        } else {
          setSelectedOrdre(findOrdreForPlate(job.plate) || buildOrdreFromAppointment(job));
        }
      }
      clickStartRef.current = null;
    },
    [],
  );

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  return (
    <div
      className="relative"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* Column headers */}
      <div className="flex flex-row" style={{ height: headerHeight }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
          }}
        />
        {visibleWorkers.map((w) => (
          <div
            key={w.id}
            className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
            style={{
              borderRight: "1px solid #D9D9D9",
              borderBottom: "1px solid #D9D9D9",
              background: "#FFF",
              color: "#525252",
              fontFamily: "Poppins",
            }}
          >
            {w.name}
          </div>
        ))}
      </div>

      <div ref={gridScrollRef} className="relative">
        {/* Car availability overlay when hovering a charge */}
        {carOverlay && (() => {
          const totalHeight = (HOURS_END - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const topPx = (carOverlay.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const bottomPx = (carOverlay.endHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const hourLabelW = 32 + 24 + 1; // hourLabel + minuteCol + border
          return (
            <>
              {/* Dim area before car arrives */}
              {topPx > 0 && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: 0,
                    left: hourLabelW,
                    right: 0,
                    height: topPx,
                    background: "rgba(0,0,0,0.06)",
                    zIndex: 2,
                  }}
                />
              )}
              {/* Green zone where car is present */}
              <div
                className="absolute pointer-events-none"
                style={{
                  top: topPx,
                  left: hourLabelW,
                  right: 0,
                  height: Math.max(bottomPx - topPx, 0),
                  background: "rgba(45, 212, 191, 0.08)",
                  borderTop: "2px solid rgba(45, 212, 191, 0.4)",
                  borderBottom: "2px solid rgba(45, 212, 191, 0.4)",
                  zIndex: 2,
                }}
              />
              {/* Dim area after car leaves */}
              {bottomPx < totalHeight && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: bottomPx,
                    left: hourLabelW,
                    right: 0,
                    height: totalHeight - bottomPx,
                    background: "rgba(0,0,0,0.06)",
                    zIndex: 2,
                  }}
                />
              )}
            </>
          );
        })()}
        <div className="flex flex-col">
          {hours.map((h) => {
            const isLunch = h === LUNCH_HOUR;
            return (
              <div
                key={h}
                className="flex flex-row"
                style={{
                  height: HOUR_BLOCK_HEIGHT,
                  borderBottom: "1px solid #D9D9D9",
                }}
              >
                <div
                  className="flex items-start text-h4 font-medium"
                  style={{
                    width: hourLabelWidth,
                    paddingLeft: 7,
                    paddingTop: 2,
                    fontFamily: "Poppins",
                    color: "#A1A1A1",
                    flexShrink: 0,
                  }}
                >
                  {h}h
                </div>
                <div
                  className="flex flex-col"
                  style={{
                    width: minuteColWidth,
                    borderLeft: "1px solid #D9D9D9",
                    borderRight: "1px solid #D9D9D9",
                    flexShrink: 0,
                  }}
                >
                  {["00", "15", "30", "45"].map((m) => (
                    <div
                      key={m}
                      className="flex items-center justify-center text-h4 font-medium"
                      style={{
                        height: 32,
                        borderBottom: "1px solid #D9D9D9",
                        fontFamily: "Poppins",
                        color: "#A1A1A1",
                      }}
                    >
                      {m}
                    </div>
                  ))}
                </div>

                {visibleWorkers.map((worker) => {
                  const colJobs = (byWorker[worker.id] || []).filter(
                    (j) => j.startHour >= h && j.startHour < h + 1,
                  );
                  const isBlocked = worker.blockedHours?.includes(h) || isBlockedByDispo(blocks, worker.id, selectedDate, h);
                  const ghost =
                    gridPreview &&
                    gridPreview.workerId === worker.id &&
                    gridPreview.startHour >= h &&
                    gridPreview.startHour < h + 1
                      ? gridPreview
                      : null;

                  return (
                    <div
                      key={worker.id}
                      data-worker-col={worker.id}
                      className="flex-1 relative"
                      style={{ borderRight: "1px solid #D9D9D9" }}
                    >
                      {[0, 1, 2, 3].map((q) => (
                        <div
                          key={q}
                          style={{
                            height: 32,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              height: 16,
                              borderBottom: "1px solid #D9D9D9",
                              background:
                                isLunch || isBlocked ? "#E6E6E6" : undefined,
                            }}
                          />
                          <div
                            style={{
                              height: 16,
                              background:
                                isLunch || isBlocked ? "#E6E6E6" : "#F4F4F4",
                              borderBottom: "1px solid #D9D9D9",
                            }}
                          />
                        </div>
                      ))}

                      {/* Grid ghost preview */}
                      {ghost &&
                        (() => {
                          const topPx =
                            ((ghost.startHour - h) / SNAP_HOUR) * SNAP_PX;
                          const heightPx =
                            (ghost.durationHours / SNAP_HOUR) * SNAP_PX;
                          const isCharge = ghost.type === "charge";

                          // Check if ghost is outside car window → red
                          let ghostOutside = false;
                          const cw = activeCarWindow;
                          if (cw?.depositDate) {
                            const depDate = cw.depositDate;
                            const restDate = cw.restitutionDate || depDate;
                            const ghostEnd = ghost.startHour + ghost.durationHours;
                            if (selectedDate < depDate || selectedDate > restDate) {
                              ghostOutside = true;
                            } else if (selectedDate === depDate && cw.depositStartHour != null && ghost.startHour < cw.depositStartHour) {
                              ghostOutside = true;
                            } else if (selectedDate === restDate && cw.restitutionStartHour != null && ghostEnd > cw.restitutionStartHour) {
                              ghostOutside = true;
                            }
                          }

                          const color = ghostOutside
                            ? "#DC2626"
                            : isCharge
                              ? "#6366F1"
                              : (() => {
                                  const j = jobs.find(
                                    (jj) =>
                                      jj.id === (ghost as JobDragPreview).jobId,
                                  );
                                  return j
                                    ? getStatusConfig(j.status).color
                                    : "#6366F1";
                                })();
                          const bg = ghostOutside
                            ? "#FEE2E2"
                            : isCharge
                              ? "#E0E6FF"
                              : (() => {
                                  const j = jobs.find(
                                    (jj) =>
                                      jj.id === (ghost as JobDragPreview).jobId,
                                  );
                                  return j
                                    ? getStatusConfig(j.status).bgColor
                                    : "#E0E6FF";
                                })();
                          return (
                            <div
                              className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                              style={{
                                top: topPx,
                                height: Math.max(heightPx, 20),
                                background: bg,
                                border: `2px dashed ${color}`,
                                borderRadius: 5,
                                margin: "0 2px",
                                zIndex: 40,
                                opacity: 0.7,
                              }}
                            >
                              <span
                                className="text-h4 font-medium"
                                style={{ color, lineHeight: "100%" }}
                              >
                                {ghostOutside && "⚠ "}
                                {formatTime(ghost.startHour)} –{" "}
                                {formatTime(
                                  ghost.startHour + ghost.durationHours,
                                )}
                              </span>
                            </div>
                          );
                        })()}

                      {/* Jobs */}
                      {colJobs.map((job) => {
                        const config = getStatusConfig(job.status);
                        const topPx =
                          ((job.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx =
                          (job.durationHours / SNAP_HOUR) * SNAP_PX;
                        const isDragSource =
                          gridPreview?.type === "job" &&
                          (gridPreview as JobDragPreview).jobId === job.id;
                        const isHovered = hoveredJobId === job.id;
                        const outsideWindow = isJobOutsideCarWindow(job);
                        return (
                          <div
                            key={job.id}
                            className="absolute left-0 right-0 flex flex-col"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: outsideWindow ? "#FEE2E2" : config.bgColor,
                              border: `1px solid ${outsideWindow ? "#FCA5A5" : config.borderColor}`,
                              boxShadow: isHovered
                                ? "0px 4px 12px rgba(0,0,0,0.25)"
                                : "0px 2px 4px rgba(0,0,0,0.15)",
                              borderRadius: 5,
                              padding: "3px 4px",
                              overflow: "hidden",
                              zIndex: isDragSource ? 5 : isHovered ? 15 : 10,
                              margin: "0 2px",
                              cursor: "grab",
                              userSelect: "none",
                              opacity: isDragSource ? 0.35 : 1,
                              transition: isDragSource
                                ? "opacity 0.15s"
                                : "box-shadow 0.15s",
                            }}
                            onPointerDown={(e) =>
                              handlePointerDown(e, job, "move")
                            }
                            onPointerUp={(e) => handlePointerUp(e, job)}
                            onMouseEnter={() => setHoveredJobId(job.id)}
                            onMouseLeave={() => setHoveredJobId(null)}
                          >
                            {/* Hover tooltip */}
                            {isHovered && !gridPreview && (
                              <div
                                className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
                                style={{ whiteSpace: "nowrap", minWidth: 140, transform: "translate(8px, 0)" }}
                                ref={(el) => {
                                  if (!el) return;
                                  const parent = el.parentElement;
                                  if (!parent) return;
                                  const rect = parent.getBoundingClientRect();
                                  el.style.top = `${rect.top}px`;
                                  el.style.left = `${rect.right}px`;
                                }}
                              >
                                <div className="text-h4 font-medium">{job.clientName}</div>
                                {job.plate && <div className="text-h5 text-neutral-300">{job.plate}</div>}
                                <div className="text-h5 text-neutral-300">
                                  {formatTime(job.startHour)} — {formatTime(job.startHour + job.durationHours)}
                                </div>
                                {outsideWindow && (
                                  <div className="text-h5 text-red-400 mt-1 font-medium">⚠ Véhicule absent à cet horaire</div>
                                )}
                                <div className="text-h5 text-neutral-400 mt-1">Cliquer pour modifier</div>
                              </div>
                            )}
                            <div
                              className="flex flex-row flex-1"
                              style={{
                                gap: 4,
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              {outsideWindow && (
                                <div className="flex items-start" style={{ flexShrink: 0 }} title="Véhicule absent à cet horaire">
                                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2L1 21h22L12 2z" fill="#EF4444" />
                                    <text x="12" y="18" textAnchor="middle" fill="#FFF" fontSize="14" fontWeight="bold">!</text>
                                  </svg>
                                </div>
                              )}
                              <span
                                className="text-h4 font-medium"
                                style={{
                                  lineHeight: "140%",
                                  color: outsideWindow ? "#DC2626" : config.color,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                              >
                                {job.clientName}
                                {job.plate ? ` ${job.plate}` : ""}
                              </span>
                              {job.hasCarIcon && (
                                <div
                                  className="flex items-start"
                                  style={{ flexShrink: 0 }}
                                >
                                  <Icon1
                                    name="car-reception"
                                    size={14}
                                    color={outsideWindow ? "#DC2626" : config.color}
                                  />
                                </div>
                              )}
                            </div>
                            <div
                              className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                              style={{ height: 8, cursor: "ns-resize" }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                handlePointerDown(e, job, "resize");
                              }}
                            >
                              <div
                                style={{
                                  width: 20,
                                  height: 2,
                                  borderRadius: 1,
                                  background: config.color,
                                  opacity: 0.4,
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
        />
      )}
    </div>
  );
}
